//@flow

import * as Immutable from 'immutable';

import { Carton } from 'common/models/flow/Carton';
import { Volume } from './Volume';
import { Dimensions } from './Dimensions';
import I18nHelper from 'common/helpers/I18nHelper';
import { VolumeUnitType } from 'common/models/internal/Volume';

import { Units } from 'common/enums/UnitEnum';
import NumberUtils from 'common/utils/NumberUtils';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import DataUtils from 'common/utils/DataUtils';

import type { CartonModelType } from 'common/models/flow/Carton';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { LineItem } from './LineItem';
const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

const preferredUnitsForWeight = [Units.list.mass.Gram, Units.list.mass.Pound];
const sourceUnitForWeight = Units.list.mass.Gram;

@validatable
class InternalCarton extends Carton {
    static fromJS(json: CartonModelType): InternalCarton {
        const state: Object = Object.assign({}, json);
        state.volume = state.volume
            ? Volume.fromJS(state.volume)
            : state.volume;
        state.dimensions = Dimensions.fromJS(state.dimensions);
        return new this(Immutable.Map(state));
    }

    get volume(): ?Volume {
        return this._state.get('volume');
    }

    get dimensions(): Dimensions {
        return this._state.get('dimensions');
    }

    @softValidateAll
    hasValidationErrors({
        lineItem,
        unitConverter
    }: ?{ lineItem: LineItem, unitConverter: UnitConversionHelper }) {
        if (!this.volume) {
            return false;
        }
        // TODO: This method has to be debugged and
        // to be implemented correctly for part volume to box volume comparison while saving
        // const volumeError = this.validateVolumeForParts(
        //     lineItem,
        //     unitConverter
        // );
        var weightError;
        if (DataUtils.isEmpty(this.weight)) {
            weightError = false;
        } else {
            weightError = this.validateWeightForParts(lineItem, unitConverter);
        }
        return (
            this.volume?.hasValidationErrors() ||
            this.dimensions?.hasValidationErrors() ||
            weightError
        );
    }

    @validateAll
    setAllValidationErrors() {
        let updatedCarton = this;
        const { volume, dimensions } = updatedCarton;
        if (volume) {
            updatedCarton = updatedCarton.setVolume(
                volume.setAllValidationErrors()
            );
        }
        if (dimensions) {
            updatedCarton = updatedCarton.setDimensions(
                dimensions.setAllValidationErrors()
            );
        }
        return updatedCarton;
    }

    @validate
    setWeight(weight: ?number): this {
        return super.setWeight(weight);
    }

    @validate
    setCapacity(capacity: ?number): this {
        return super.setCapacity(capacity);
    }

    validateCapacity() {
        if (DataUtils.isEmpty(this.capacity)) {
            return nsTranslate('cartonInput.capacityNotEmpty');
        }
        if (!NumberUtils.isPositiveNumber(this.capacity)) {
            return nsTranslate('cartonInput.invalidCapacity');
        }
    }

    validateWeight() {
        if (
            !DataUtils.isEmpty(this.weight) &&
            !NumberUtils.isPositiveNumber(this.weight)
        ) {
            return nsTranslate('cartonInput.invalidWeight');
        }
    }

    validateWeightForParts(
        lineItem: Immutable.List<LineItem>,
        unitConverter: UnitConversionHelper
    ) {
        let totalPartsWeight = 0;
        var cartonWeight = this.convertToSourceUnit(unitConverter);
        totalPartsWeight = lineItem.getTotalPartsWeight();
        return totalPartsWeight * Number(this.capacity) >
            Number(cartonWeight.weight)
            ? nsTranslate('cartonInput.invalidBoxWeight')
            : null;
    }
    validateVolumeForParts(
        lineItem: Immutable.List<LineItem>,
        unitConverter: UnitConversionHelper
    ) {
        const cartonVolume = this.getCartonVolume(unitConverter);
        const partsVolume = lineItem.getTotalPartsVolume(unitConverter);
        if (partsVolume * Number(this.capacity) > cartonVolume) {
            return this.volume?.unit === VolumeUnitType.LBHMeters
                ? nsTranslate('cartonInput.invalidBoxDimensions')
                : nsTranslate('cartonInput.invalidBoxVolume');
        } else {
            return null;
        }
    }

    getCartonVolume(unitConverter: UnitConversionHelper) {
        let totalCartonVolume = 0;
        if (this.volume && !DataUtils.isEmpty(this.volume.value)) {
            if (!this.volume.hasValidationErrors()) {
                totalCartonVolume = this.volume.getTotalVolume(unitConverter);
            }
        }
        return totalCartonVolume;
    }

    getPreferredUnitForWeight(unitConverter: UnitConversionHelper) {
        return unitConverter.getPreferredUnit({
            preferredUnits: preferredUnitsForWeight,
            sourceUnit: sourceUnitForWeight
        });
    }

    getPreferredUnitTextForWeight(unitConverter: UnitConversionHelper) {
        return unitConverter.getConvertedUnitDisplayText({
            preferredUnits: preferredUnitsForWeight,
            sourceUnit: sourceUnitForWeight,
            pluralizeUnit: false
        });
    }

    convertToPreferredUnit(unitConverter: UnitConversionHelper): this {
        let updatedCarton = this;
        const { weight, volume, dimensions } = this;
        if (NumberUtils.isNumeric(weight)) {
            const convertWeight = value =>
                unitConverter.convertValue({
                    value: NumberUtils.toNumber(value),
                    sourceUnit: sourceUnitForWeight,
                    preferredUnits: preferredUnitsForWeight
                }).value;
            updatedCarton = updatedCarton.setWeight(convertWeight(weight));
        }
        if (volume) {
            updatedCarton = updatedCarton.setVolume(
                volume.convertToPreferredUnit(unitConverter)
            );
        }

        if (dimensions) {
            updatedCarton = updatedCarton.setDimensions(
                dimensions.convertToPreferredUnit(unitConverter)
            );
        }

        return updatedCarton;
    }

    convertToSourceUnit(unitConverter: UnitConversionHelper): this {
        let updatedCarton = this;
        const { weight, volume, dimensions } = this;
        if (NumberUtils.isNumeric(weight)) {
            const preferredUnit = this.getPreferredUnitForWeight(unitConverter);
            const convertWeight = value =>
                unitConverter.basicConvert({
                    value: NumberUtils.toNumber(value),
                    sourceUnit: preferredUnit,
                    targetUnit: sourceUnitForWeight
                });
            updatedCarton = updatedCarton.setWeight(convertWeight(weight));
        }
        if (volume && !volume.hasValidationErrors()) {
            updatedCarton = updatedCarton.setVolume(
                volume.convertToSourceUnit(unitConverter)
            );
        }

        if (dimensions && !dimensions.hasValidationErrors()) {
            updatedCarton = updatedCarton.setDimensions(
                dimensions.convertToSourceUnit(unitConverter)
            );
        }

        return updatedCarton;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedCarton = super.defaultSanitize();

        if (sanitizedCarton) {
            const sanitizedVolume = sanitizedCarton.volume?.defaultSanitize();
            const sanitizedDimensions = sanitizedCarton.dimensions?.defaultSanitize();
            return sanitizedCarton
                .setDimensions(
                    sanitizedDimensions || sanitizedCarton.dimensions
                )
                .setVolume(sanitizedVolume);
        }
        return sanitizedCarton;
    }

    static defaultInit({ props }): InternalCarton {
        const volume = Volume.defaultInit({
            unit: VolumeUnitType.MetersCubic,
            value: null
        });
        const dimensions = Dimensions.defaultInit({ props });

        return InternalCarton.fromJS({})
            .setDimensions(dimensions)
            .setVolume(volume);
    }
}

export { InternalCarton as Carton };
